import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';
import type { FeatureCollection } from 'geojson';
import {
  claudiaTreePath,
  debugRiskOf,
  isPointPart,
  rawRowsByPart,
  WHISP_DEBUG_COLORS,
  whispTimberStep,
  WhispDebugGroup,
  WhispDebugPart,
  WhispDebugView,
} from './whisp-debug';

interface PartRow {
  part: WhispDebugPart;
  color: string;
}

interface GroupRow {
  group: WhispDebugGroup;
  parts: PartRow[];
}

const INDICATOR_KEY_PREFIX = 'whisp_indicator_';

@Component({
  selector: 'app-whisp-debug-panel',
  imports: [DecimalPipe],
  templateUrl: './whisp-debug-panel.component.html',
  styleUrl: './whisp-debug-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'mapboxgl-ctrl' },
})
export class WhispDebugPanelComponent {
  private readonly translate = inject(TranslateService);

  readonly view = input<WhispDebugView | null>(null);
  readonly siteGeo = input<FeatureCollection | null>(null);
  readonly selectedPartId = input<string | null>(null);
  readonly selectPart = output<string>();

  readonly groups = computed<GroupRow[]>(() => {
    const finding = this.view()?.finding;
    const parts = new Map((finding?.parts ?? []).map((part) => [part.partId, part]));
    const grouped = (finding?.groups ?? []).map((group) => ({
      group,
      parts: group.partIds.flatMap((partId) => (parts.has(partId) ? [this.rowOf(parts.get(partId)!)] : [])),
    }));
    const inGroup = new Set(grouped.flatMap((row) => row.group.partIds));
    const loose = [...parts.values()].filter((part) => !inGroup.has(part.partId));
    if (!loose.length) return grouped;
    return [
      ...grouped,
      {
        group: { groupId: -1, grade: 'ERROR', partIds: loose.map((part) => part.partId) },
        parts: loose.map((part) => this.rowOf(part)),
      },
    ];
  });

  readonly selected = computed(() => {
    const partId = this.selectedPartId();
    const part = this.view()?.finding?.parts.find((candidate) => candidate.partId === partId);
    if (!part) return null;
    const indicators = part.indicators ?? {};
    const step = whispTimberStep(indicators);
    const tree = claudiaTreePath(indicators);
    const row = rawRowsByPart(this.view()?.raw).get(part.partId);
    return {
      part,
      step,
      tree,
      stepMismatch: !!part.riskTimber && step.risk !== part.riskTimber,
      treeMismatch: !!part.riskTimber && tree.risk !== part.riskTimber,
      point: isPointPart(this.siteGeo(), part),
      indicators: Object.entries(indicators)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, on]) => ({ key, label: this.indicatorLabel(key), on })),
      keyHa: Object.entries(part.keyHa ?? {}),
      riskPcrop: row?.properties?.['risk_pcrop'],
      riskAcrop: row?.properties?.['risk_acrop'],
      rawRow: Object.entries(row?.properties ?? {})
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, value]) => ({ key, value: typeof value === 'object' ? JSON.stringify(value) : String(value) })),
      call: (this.view()?.calls ?? []).findIndex((call) => call.ownPartIds.includes(part.partId)) + 1,
    };
  });

  colorOf(risk: string | null | undefined): string {
    return WHISP_DEBUG_COLORS[debugRiskOf({ riskTimber: risk })];
  }

  private rowOf(part: WhispDebugPart): PartRow {
    return { part, color: WHISP_DEBUG_COLORS[debugRiskOf(part)] };
  }

  private indicatorLabel(key: string): string {
    const translationKey = `${INDICATOR_KEY_PREFIX}${key}_tr`;
    const label = this.translate.instant(translationKey);
    return label === translationKey ? key : label;
  }
}
